import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HowToVoteIcon from '@mui/icons-material/HowToVote'
import AddIcon from '@mui/icons-material/Add'
import FeatureRequestList from './FeatureRequestList'
import FeatureRequestForm from './FeatureRequestForm'

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

function FeatureVotingDialog({ firebase, toggle, isOpen }) {
  const [tabValue, setTabValue] = useState(0)
  const [requests, setRequests] = useState([])
  const [userVotes, setUserVotes] = useState([])
  const [voteBalance, setVoteBalance] = useState({
    totalVotes: 3,
    usedVotes: 0,
    remainingVotes: 3,
  })
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const loadData = useCallback(async () => {
    if (!isOpen) return
    setLoading(true)

    try {
      const [balanceData, votesData] = await Promise.all([
        firebase.getFeatureVoteBalance(),
        firebase.getUserFeatureVotes(),
      ])

      setVoteBalance(balanceData)
      setUserVotes(votesData)
    } catch (error) {
      console.error('Error loading vote data:', error)
    } finally {
      setLoading(false)
    }
  }, [firebase, isOpen])

  useEffect(() => {
    if (!isOpen) return

    loadData()

    const unsubscribe = firebase.listenApprovedFeatureRequests((requestsData) => {
      setRequests(requestsData)
      setLoading(false)
    })

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [isOpen, firebase, loadData])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleVote = async (requestId, voteCount) => {
    try {
      await firebase.castFeatureVote(requestId, voteCount)
      await loadData()
      setSnackbar({ open: true, message: 'Ääni annettu!', severity: 'success' })
    } catch (error) {
      console.error('Vote error:', error)
      setSnackbar({ open: true, message: 'Äänestys epäonnistui', severity: 'error' })
    }
  }

  const handleSubmitSuccess = () => {
    setSnackbar({
      open: true,
      message: 'Kiitos toiveestasi! Se siirtyy nyt moderointiin.',
      severity: 'success',
    })
    setTabValue(0)
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={toggle}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <HowToVoteIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Ominaisuustoiveet</Typography>
            </Box>
            <IconButton onClick={toggle} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Äänestä ominaisuustoiveita, joita haluaisit nähdä toteutettavan järjestelmään ja ovat sinulle tärkeimpiä. Sinulla on käytössäsi{' '}
            <strong>3 ääntä kuukaudessa</strong>, jotka voit jakaa haluamallesi määrälle toiveita
            (max 3 ääntä per toive).
          </Alert>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                icon={<HowToVoteIcon />}
                iconPosition="start"
                label="Selaa ja äänestä"
              />
              <Tab icon={<AddIcon />} iconPosition="start" label="Lähetä toive" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <FeatureRequestList
              requests={requests}
              loading={loading}
              voteBalance={voteBalance}
              userVotes={userVotes}
              onVote={handleVote}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <FeatureRequestForm
              firebase={firebase}
              onSubmitSuccess={handleSubmitSuccess}
              onCancel={() => setTabValue(0)}
            />
          </TabPanel>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FeatureVotingDialog
